import React from "react";
import { CheckIcon } from "lucide-react";
import { cn } from "../../utils";
import { Button } from "../core/button";

export interface StepProps {
  title: string;
  isComplete?: boolean;
  inProgress?: boolean;
}

export type MultiStepMode = "wizard" | "free";

export interface MultiStepLabels {
  completed?: string;
  current?: string;
  pending?: string;
}

export interface MultiStepProps {
  steps: StepProps[];
  currentStep?: number;
  onChange?: (step: number) => void;
  /**
   * `wizard` (default, creating): steps unlock in order — only indexes up to
   * `reachable` can be clicked, the rest are greyed out.
   * `free` (editing): every step is already saved data, so all are clickable
   * and no completed / pending captions are shown.
   */
  mode?: MultiStepMode;
  /** Wizard only: highest step index the user may jump to (default: the current one). */
  reachable?: number;
  /** Captions under the titles (wizard only). Defaults are English. */
  labels?: MultiStepLabels;
}

/** Pure: may the user click step `index`? */
export const canReachStep = (index: number, opts: { mode: MultiStepMode; currentStep: number; reachable?: number }): boolean => {
  if (opts.mode === "free") return true;
  return index <= (opts.reachable ?? opts.currentStep);
};

export const MultiStep: React.FC<MultiStepProps> = ({
  steps,
  currentStep = 0,
  onChange,
  mode = "wizard",
  reachable,
  labels,
}) => {
  const captions = {
    completed: labels?.completed ?? "Completed",
    current: labels?.current ?? "In progress",
    pending: labels?.pending ?? "Pending",
  };
  // Process steps based on currentStep
  const processedSteps = steps.map((step, index) => {
    let isComplete = false;
    let inProgress = false;

    if (mode === "wizard" && index < currentStep) {
      isComplete = true;
    } else if (index === currentStep) {
      inProgress = true;
    }

    const clickable = !!onChange && canReachStep(index, { mode, currentStep, reachable });
    return { ...step, isComplete, inProgress, clickable };
  });

  const handleStepClick = (index: number) => {
    if (onChange) {
      onChange(index);
    }
  };

  return (
    <nav
      aria-label="Progress"
      className="w-full rounded-xl border border-border bg-card/80 px-6 py-4 shadow-sm backdrop-blur-sm"
    >
      <ol className="flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-0">
        {processedSteps.map((step, index) => {
          const stepNumber = index + 1;
          const statusLabel =
            mode === "free"
              ? null
              : step.isComplete
                ? captions.completed
                : step.inProgress
                  ? captions.current
                  : captions.pending;

          return (
            <li
              key={step.title}
              className={cn(
                "relative flex items-center gap-4",
                index < processedSteps.length - 1 && "sm:flex-1"
              )}
            >
              <Button
                type="button"
                variant="ghost"
                onClick={step.clickable ? () => handleStepClick(index) : undefined}
                disabled={!step.clickable}
                aria-current={step.inProgress ? "step" : undefined}
                aria-disabled={!step.clickable || undefined}
                className={cn(
                  "group flex h-auto w-full items-center justify-start gap-4 whitespace-normal rounded-none p-0 text-left font-normal hover:bg-transparent hover:text-inherit disabled:pointer-events-auto disabled:opacity-100",
                  !step.clickable && (onChange ? "cursor-not-allowed opacity-60" : "cursor-default")
                )}
              >
                <span
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all duration-200",
                    step.isComplete
                      ? "border-primary bg-primary text-primary-foreground shadow-sm"
                      : step.inProgress
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-muted text-muted-foreground"
                  )}
                >
                  {step.isComplete ? <CheckIcon className="h-4 w-4" /> : stepNumber}
                </span>

                <div className="space-y-0.5">
                  <p
                    className={cn(
                      "text-sm font-medium",
                      step.isComplete
                        ? "text-primary"
                        : step.inProgress
                          ? "text-foreground"
                          : "text-muted-foreground"
                    )}
                  >
                    {step.title}
                  </p>
                  {statusLabel && <p className="text-xs text-muted-foreground">{statusLabel}</p>}
                </div>
              </Button>

              {index < processedSteps.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "hidden h-px flex-1 rounded-full sm:block",
                    mode === "wizard" && index < currentStep
                      ? "bg-primary"
                      : index === currentStep
                        ? "bg-gradient-to-r from-primary/80 via-border to-border"
                        : "bg-border"
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

// Remove the example usage - parent components will define their own steps
